import { useCallback, useEffect, useRef } from 'react'

import {
  Accordion,
  AccordionItem,
  Divider,
  CloseIcon,
  ArrowRightIcon,
  LoanIcon,
  ApartmentIcon,
  Text
} from '@cash-me/react-components'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { AnchorButton } from 'components/anchor-button'
import { Logo } from 'components/logo'
import useClickOutside from 'hooks/useClickOutside'
import { pushDataLayer } from 'utils/push-data-layer'

import { SidebarProps } from './sidebar.model'
import * as S from './styles'

export const Sidebar = ({ showSidebar, close }: SidebarProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { asPath } = router
  const urlPartner = asPath.includes('/condo/')
    ? process.env.NEXT_PUBLIC_CONDO_URL
    : process.env.NEXT_PUBLIC_PARTNER_URL

  const handleDataLayer = useCallback((section: string, name: string) => {
    pushDataLayer({
      category: 'Clique',
      action: 'Header',
      label: `${section} - Link ${name}`
    })
  }, [])

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()

        close()
      }
    },
    [close]
  )

  const handleClickOutside = useCallback(() => {
    close()
  }, [close])

  useClickOutside(ref, handleClickOutside)

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp, false)

    return () => {
      window.removeEventListener('keyup', handleKeyUp, false)
      close()
    }
  }, [handleKeyUp, close])

  return (
    <S.Wrapper data-show={showSidebar} aria-hidden={showSidebar}>
      <S.Content ref={ref}>
        <S.CloseButton aria-label="Fechar menu lateral" onClick={close}>
          <CloseIcon color="var(--colors-blue-text-strong)" size={28} />
        </S.CloseButton>
        <S.Menu>
          <S.ButtonsWrapper>
            <AnchorButton
              href="/sou-cliente/"
              prefetch={false}
              size="sm"
              dataLayer={{
                category: 'Clique',
                action: 'Header',
                label: 'Sou cliente - Link Sou cliente'
              }}
            >
              Sou cliente
            </AnchorButton>
            <AnchorButton
              href={urlPartner!}
              prefetch={false}
              size="sm"
              color="white"
              dataLayer={{
                category: 'Clique',
                action: 'Header',
                label: 'Sou parceiro - Link Sou parceiro'
              }}
            >
              Sou parceiro
            </AnchorButton>
          </S.ButtonsWrapper>
          <S.SidebarItem>
            <Text size="heading4" weight="medium">
              Seja nosso parceiro
            </Text>
            <Text size="body2" color="var(--colors-neutral-text-weaker)">
              Ofereça valor para seus clientes
            </Text>
            <NextLink
              href="/seja-um-parceiro"
              prefetch={false}
              onClick={() => {
                handleDataLayer('Seja parceiro', 'Seja parceiro')
              }}
            >
              <Text size="body2" color="var(--colors-blue-text-default)">
                Seja nosso parceiro
              </Text>
              <ArrowRightIcon size={12} />
            </NextLink>
          </S.SidebarItem>
          <Divider />
          <Accordion type="multiple" className="accordion-support">
            <AccordionItem
              id="sidebar-0"
              className="accordion-item"
              title={
                <Text size="heading4" weight="medium">
                  <LoanIcon color="var(--colors-cyan-icon-default)" size={24} /> Para você
                </Text>
              }
            >
              <S.AccordionLinks>
                <NextLink
                  href="/emprestimo-com-garantia-de-imovel"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para você', 'Empréstimo com Garantia de Imóvel')
                  }}
                  className="ds-link"
                >
                  Empréstimo com Garantia de Imóvel
                </NextLink>
                <NextLink
                  href="/financiamento-para-construcao"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para você', 'Financiamento para Construção')
                  }}
                  className="ds-link"
                >
                  Financiamento para Construção
                </NextLink>
                <NextLink
                  href="/financiamento-imobiliario"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para você', 'Financiamento Imobiliário')
                  }}
                  className="ds-link"
                >
                  Financiamento Imobiliário (SFI)
                </NextLink>
              </S.AccordionLinks>
            </AccordionItem>
            <AccordionItem
              id="sidebar-1"
              className="accordion-item"
              title={
                <Text size="heading4" weight="medium">
                  <Logo type="IconBusiness" width={1.5} height={1.5} className="business-icon" /> Para seu negócio
                </Text>
              }
            >
              <S.AccordionLinks>
                <NextLink
                  href="/emprestimo-com-garantia-de-imovel"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para seu negócio', 'Empréstimo com Garantia de Imóvel')
                  }}
                  className="ds-link"
                >
                  Empréstimo com Garantia de Imóvel
                </NextLink>
                <NextLink
                  href="/emprestimo-para-empresa"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para seu negócio', 'Empréstimo para Empresa')
                  }}
                  className="ds-link"
                >
                  Empréstimo para Empresas
                </NextLink>
                <NextLink
                  href="/emprestimo-para-capital-de-giro"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para seu negócio', 'Empréstimo para Capital de Giro')
                  }}
                  className="ds-link"
                >
                  Empréstimo para Capital de Giro
                </NextLink>
                <NextLink
                  href="/financiamento-para-construcao"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para seu negócio', 'Financiamento para Construção')
                  }}
                  className="ds-link"
                >
                  Financiamento para Construção
                </NextLink>
              </S.AccordionLinks>
            </AccordionItem>
            <AccordionItem
              id="sidebar-2"
              className="accordion-item"
              title={
                <Text size="heading4" weight="medium">
                  <ApartmentIcon color="var(--colors-cyan-icon-default)" size={24} /> Para seu condomínio
                </Text>
              }
            >
              <S.AccordionLinks>
                <NextLink
                  href="/condo"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para seu condomínio', 'Empréstimo para condomínio')
                  }}
                  className="ds-link"
                >
                  Empréstimo para Condomínio
                </NextLink>
                <NextLink
                  href="/financiamento-energia-solar"
                  prefetch={false}
                  onClick={() => {
                    handleDataLayer('Para seu condomínio', 'Financiamento para Energia Solar')
                  }}
                  className="ds-link"
                >
                  Financiamento para Energia Solar
                </NextLink>
              </S.AccordionLinks>
            </AccordionItem>
          </Accordion>
          <Divider className="divider" />
          <Text size="caption" weight="bold" uppercase color="var(--colors-neutral-text-weaker)">
            A CashMe
          </Text>
          <NextLink
            href="/quem-somos"
            prefetch={false}
            onClick={() => {
              handleDataLayer('A CashMe', 'Sobre a CashMe')
            }}
          >
            <Text size="body2" color="var(--colors-neutral-text-weak)">
              Sobre a CashMe
            </Text>
          </NextLink>
          <NextLink
            href="/fale-conosco"
            prefetch={false}
            onClick={() => {
              handleDataLayer('A CashMe', 'Fale Conosco')
            }}
          >
            <Text size="body2" color="var(--colors-neutral-text-weak)">
              Fale Conosco
            </Text>
          </NextLink>
          <NextLink
            href="/programa-de-integridade"
            prefetch={false}
            onClick={() => {
              handleDataLayer('A CashMe', 'Programa de Integridade')
            }}
          >
            <Text size="body2" color="var(--colors-neutral-text-weak)">
              Programa de Integridade
            </Text>
          </NextLink>
          <NextLink
            href="/seguranca"
            prefetch={false}
            onClick={() => {
              handleDataLayer('A CashMe', 'Dicas de Segurança')
            }}
          >
            <Text size="body2" color="var(--colors-neutral-text-weak)">
              Dicas de Segurança
            </Text>
          </NextLink>
          <NextLink
            href="https://www.cashme.com.br/blog/"
            prefetch={false}
            onClick={() => {
              handleDataLayer('A CashMe', 'Blog')
            }}
          >
            <Text size="body2" color="var(--colors-neutral-text-weak)">
              Blog CashMe
            </Text>
          </NextLink>
          <Divider className="divider" />
          <Text size="caption" weight="bold" uppercase color="var(--colors-neutral-text-weaker)">
            Ajuda
          </Text>
          <S.SidebarItem>
            <Text size="heading4" weight="medium">
              Fale com a gente
            </Text>
            <Text size="body2" color="var(--colors-neutral-text-weaker)">
              A nossa equipe está pronta para te ajudar
            </Text>
            <NextLink
              href="/fale-conosco"
              prefetch={false}
              onClick={() => {
                handleDataLayer('Ajuda', 'Fale com a gente')
              }}
            >
              <Text size="body2" color="var(--colors-blue-text-default)">
                Saiba mais
              </Text>
              <ArrowRightIcon size={12} />
            </NextLink>
          </S.SidebarItem>
          <S.SidebarItem>
            <Text size="heading4" weight="medium">
              Precisa de ajuda?
            </Text>
            <Text size="body2" color="var(--colors-neutral-text-weaker)">
              Veja quais dúvidas outras pessoas também tiveram
            </Text>
            <NextLink
              href="/ajuda"
              prefetch={false}
              onClick={() => {
                handleDataLayer('Ajuda', 'Ajuda')
              }}
            >
              <Text size="body2" color="var(--colors-blue-text-default)">
                Saiba mais
              </Text>
              <ArrowRightIcon size={12} />
            </NextLink>
          </S.SidebarItem>
        </S.Menu>
      </S.Content>
    </S.Wrapper>
  )
}
